import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handleSubscribe = async (tier: string) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (tier === 'enterprise') {
      window.location.href = `mailto:${process.env.ENTERPRISE_EMAIL || 'enterprise@example.com'}?subject=Enterprise Plan Inquiry`;
      return;
    }

    setIsLoading(tier);

    try {
      const response = await fetch(`${API_BASE_URL}/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <Layout>
      <SEO
        title="Pricing Plans - B2B CSV | Affordable Lead Generation"
        description="Choose the perfect plan for your business. From free to enterprise, get access to millions of B2B leads with CSV export. Transparent pricing with no hidden fees."
        canonical="/pricing"
      />
      <div className="bg-slate-900">
        {/* Header */}
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-cyan-400 tracking-wide uppercase">Pricing</h2>
            <p className="mt-1 text-4xl font-extrabold text-slate-100 sm:text-5xl sm:tracking-tight lg:text-6xl">
              Choose the right plan for you
            </p>
            <p className="max-w-xl mt-5 mx-auto text-xl text-slate-400">
              Start with our free plan and upgrade as you grow. No hidden fees.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="mt-16 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-x-8">
            {/* Free Plan */}
            <div className="relative p-8 bg-slate-800 border border-slate-700 rounded-2xl shadow-lg flex flex-col">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-100">Free</h3>
                <p className="mt-4 flex items-baseline text-slate-100">
                  <span className="text-5xl font-extrabold tracking-tight">$0</span>
                  <span className="ml-1 text-xl font-semibold">/month</span>
                </p>
                <p className="mt-6 text-slate-400">Perfect for trying out our platform</p>

                <ul className="mt-6 space-y-4">
                  <PricingFeature text="10 searches per month" />
                  <PricingFeature text="100 exports per month" />
                  <PricingFeature text="AI-powered search" />
                  <PricingFeature text="CSV & Sheets export" />
                  <PricingFeature text="Basic enrichment" />
                  <PricingFeature text="No credit card required" included />
                </ul>
              </div>

              <Link
                to="/register"
                className="mt-8 block w-full bg-slate-700 hover:bg-slate-600 text-white rounded-md py-3 text-sm font-semibold text-center transition-colors"
              >
                Get started
              </Link>
            </div>

            {/* Starter Plan */}
            <div className="relative p-8 bg-slate-800 border border-slate-700 rounded-2xl shadow-lg flex flex-col">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-100">Starter</h3>
                <p className="absolute top-0 py-1.5 px-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
                  Most Popular
                </p>
                <p className="mt-4 flex items-baseline text-slate-100">
                  <span className="text-5xl font-extrabold tracking-tight">$24.99</span>
                  <span className="ml-1 text-xl font-semibold">/month</span>
                </p>
                <p className="mt-6 text-slate-400">For professionals and small teams</p>

                <ul className="mt-6 space-y-4">
                  <PricingFeature text="100 searches per month" />
                  <PricingFeature text="1,000 exports per month" />
                  <PricingFeature text="All Free features" />
                  <PricingFeature text="CSV & Sheets export" included />
                  <PricingFeature text="AI enrichment" included />
                  <PricingFeature text="Priority support" included />
                </ul>
              </div>

              <button
                onClick={() => handleSubscribe('starter')}
                disabled={isLoading === 'starter'}
                className="mt-8 block w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-md py-3 text-sm font-semibold text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'starter' ? 'Processing...' : user ? 'Subscribe now' : 'Get started'}
              </button>
            </div>

            {/* Pro Plan */}
            <div className="relative p-8 bg-slate-800 border-2 border-cyan-500 rounded-2xl shadow-xl flex flex-col">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-100">Pro</h3>
                <p className="absolute top-0 py-1.5 px-4 bg-green-500 text-white rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
                  Best Value
                </p>
                <p className="mt-4 flex items-baseline text-slate-100">
                  <span className="text-5xl font-extrabold tracking-tight">$59.99</span>
                  <span className="ml-1 text-xl font-semibold">/month</span>
                </p>
                <p className="mt-6 text-slate-400">For growing businesses</p>

                <ul className="mt-6 space-y-4">
                  <PricingFeature text="300 searches per month" />
                  <PricingFeature text="3,000 exports per month" />
                  <PricingFeature text="All Starter features" />
                  <PricingFeature text="Advanced AI enrichment" included />
                  <PricingFeature text="Email & social profiles" included />
                  <PricingFeature text="Premium support" included />
                  <PricingFeature text="API access (coming soon)" />
                </ul>
              </div>

              <button
                onClick={() => handleSubscribe('pro')}
                disabled={isLoading === 'pro'}
                className="mt-8 block w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-md py-3 text-sm font-semibold text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'pro' ? 'Processing...' : user ? 'Subscribe now' : 'Get started'}
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="relative p-8 bg-slate-800 border-2 border-purple-500 rounded-2xl shadow-xl flex flex-col">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-100">Enterprise</h3>
                <p className="absolute top-0 py-1.5 px-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
                  Maximum Power
                </p>
                <p className="mt-4 flex items-baseline text-slate-100">
                  <span className="text-5xl font-extrabold tracking-tight">$299</span>
                  <span className="ml-1 text-xl font-semibold">/month</span>
                </p>
                <p className="mt-6 text-slate-400">For large organizations</p>

                <ul className="mt-6 space-y-4">
                  <PricingFeature text="1,500 searches per month" />
                  <PricingFeature text="15,000 exports per month" />
                  <PricingFeature text="250 enrichments per month" />
                  <PricingFeature text="Google Sheets export" included />
                  <PricingFeature text="All Pro features" />
                  <PricingFeature text="Custom integrations" included />
                  <PricingFeature text="Dedicated account manager" included />
                  <PricingFeature text="SLA guarantee" included />
                </ul>
              </div>

              <button
                onClick={() => handleSubscribe('enterprise')}
                className="mt-8 block w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-md py-3 text-sm font-semibold text-center transition-all"
              >
                Contact sales
              </button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24">
            <h3 className="text-3xl font-extrabold text-slate-100 text-center">Frequently asked questions</h3>
            <dl className="mt-12 space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12">
              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  Can I change plans later?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  What payment methods do you accept?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  We accept all major credit cards through Stripe. Your payment information is secure and encrypted.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  Is there a free trial?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  Our Free plan is always available with 10 searches and 100 exports per month. No credit card required to start.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  Can I cancel anytime?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  Do unused results roll over?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  No, unused results reset at the end of each billing period. We recommend choosing a plan that fits your monthly needs.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-slate-100">
                  Need help choosing?
                </dt>
                <dd className="mt-2 text-base text-slate-400">
                  Contact our team and we'll help you find the perfect plan for your needs.
                </dd>
              </div>
            </dl>
          </div>

          {/* Comparison Table */}
          <div className="mt-24">
            <h3 className="text-3xl font-extrabold text-slate-100 text-center mb-12">Feature comparison</h3>
            <div className="overflow-hidden shadow ring-1 ring-slate-700 rounded-lg">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-800">
                  <tr>
                    <th scope="col" className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-slate-100">Feature</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Free</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Starter</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Pro</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700 bg-slate-800/50">
                  <ComparisonRow feature="Searches per month" free="10" starter="100" pro="300" enterprise="1,500" />
                  <ComparisonRow feature="Exports per month" free="100" starter="1,000" pro="3,000" enterprise="15,000" />
                  <ComparisonRow feature="AI search optimization" free="✓" starter="✓" pro="✓" enterprise="✓" />
                  <ComparisonRow feature="CSV & Sheets export" free="✓" starter="✓" pro="✓" enterprise="✓" />
                  <ComparisonRow feature="AI enrichment" free="Basic" starter="✓" pro="Advanced" enterprise="Advanced" />
                  <ComparisonRow feature="API access" free="—" starter="—" pro="Soon" enterprise="✓" />
                  <ComparisonRow feature="Custom integrations" free="—" starter="—" pro="—" enterprise="✓" />
                  <ComparisonRow feature="Dedicated support" free="—" starter="—" pro="✓" enterprise="✓" />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function PricingFeature({ text, included = false }: { text: string; included?: boolean }) {
  return (
    <li className="flex items-start">
      <div className="flex-shrink-0">
        <svg className={`h-6 w-6 ${included ? 'text-green-400' : 'text-cyan-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="ml-3 text-sm text-slate-300">{text}</p>
    </li>
  );
}

function ComparisonRow({ feature, free, starter, pro, enterprise }: { feature: string; free: string; starter: string; pro: string; enterprise: string }) {
  return (
    <tr>
      <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-slate-100">{feature}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">{free}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">{starter}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">{pro}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">{enterprise}</td>
    </tr>
  );
}
